/**
 * SupplyPreviewStep (Phase 92, UI-SPEC 1.2 step 3 "Check the print SKUs").
 *
 * Renders the composer's LAST preview result (`useSupplyComposer`), never a
 * second call:
 *   - "Every variant becomes" over `SkuAnatomy size="md"`, built from the
 *     server's anatomy (the codes stay server-side, D-15);
 *   - the preview table (Variant, Print SKU, Blank, Status), inside a
 *     `max-h-80 overflow-y-auto` box above 12 rows (UI 22), never sideways;
 *   - one `WarningNotice` for the variants that will not sync (no blank in
 *     that size and colour, print SKU in use, left out, other reasons), each
 *     reason listing up to 3 variants then "and {n} more" (UI 16);
 *   - two `NoteLine`s: what comes from the 3PL's blank (D-06), and that stock
 *     is the 3PL's shared pool other stores can also sell from, without naming
 *     any other store (D-07).
 * While the preview builds: a shimmering anatomy and skeleton rows, never a
 * page spinner (UI 8). A failed preview: `SectionError compact` with "Build
 * print SKUs again" (UI 11).
 *
 * No actions live here: the footer (save, discard) is the composer's and is
 * admin only, so this body is safe to show anyone who may read the product.
 */
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Boxes, Link2 } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { StatusPill } from '@/components/shared/StatusPill';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { cn } from '@/lib/utils';

import { SkuAnatomy, type AnatomySegment } from '../SkuAnatomy';
import type { SupplyComposePreviewOk, SupplyPreviewVariantRow } from '../../hooks/useSupplyComposer';

/** UI 22: above this many rows the table scrolls inside its own box. */
export const SUPPLY_PREVIEW_BOX_ROWS = 12;
/** UI 16: names listed per reason before "and {n} more". */
const LIST_MAX = 3;

export interface SupplyPreviewStepProps {
  /** The last complete preview, or null while none has arrived. */
  preview: SupplyComposePreviewOk | null;
  ownerName: string;
  /** A preview request is pending (the debounce included). */
  loading: boolean;
  /** The last preview request failed. */
  error: boolean;
  onRetry: () => void;
  /** Unique per product, for ids. */
  idPrefix: string;
}

function variantLabel(t: TFunction<'productSync'>, row: SupplyPreviewVariantRow): string {
  const parts = [row.storeSize, row.storeColour].filter((p): p is string => typeof p === 'string' && p.trim() !== '');
  return parts.length > 0 ? parts.join(' / ') : t('supply.preview.onlyVariant');
}

function locationOf(preview: SupplyComposePreviewOk, row: SupplyPreviewVariantRow): string | null {
  return row.locationId ? (preview.locations.find((l) => l.id === row.locationId)?.name ?? null) : null;
}

/** Up to 3 names, then "and {n} more" (the Tier 1 duplicate-SKU pattern). */
function nameList(t: TFunction<'productSync'>, names: string[]): string {
  const shown = names.slice(0, LIST_MAX).join(t('supply.preview.listSeparator'));
  const rest = names.length - LIST_MAX;
  return rest > 0 ? `${shown} ${t('tier1.duplicateSkusMore', { count: rest })}` : shown;
}

/** The words under a held-back status: why this variant will not sync. */
function heldBackReason(t: TFunction<'productSync'>, row: SupplyPreviewVariantRow): string {
  switch (row.reason) {
    case 'no_blank_combination':
      return t('supply.preview.reason.noBlank');
    case 'code_clash':
      return t('supply.preview.reason.codeClash', { code: row.blank?.code ?? '' });
    case 'invalid_design_code':
      return t('job.blocker.invalid_design_code');
    case 'invalid_blank_code':
      return t('supply.preview.reason.invalidBlankCode');
    case 'duplicate_print_sku':
      return t('job.blocker.duplicate_derived_sku');
    default:
      return t('supply.preview.heldBack');
  }
}

function inUseLine(t: TFunction<'productSync'>, preview: SupplyComposePreviewOk, row: SupplyPreviewVariantRow): string {
  const location = locationOf(preview, row);
  if (row.printSku && location) return t('supply.refusal.printSkuInUse', { sku: row.printSku, location });
  if (row.printSku) return t('supply.refusal.printSkuInUseSomewhere', { sku: row.printSku });
  return t('supply.refusal.printSkuInUseUnknown');
}

function anatomySegments(t: TFunction<'productSync'>, anatomy: NonNullable<SupplyComposePreviewOk['anatomy']>): AnatomySegment[] {
  const label: Record<'type' | 'size' | 'colour' | 'design', string> = {
    type: t('anatomy.type'),
    size: t('anatomy.size'),
    colour: t('anatomy.colour'),
    design: anatomy.prefix === 'CUST-' ? t('anatomy.auto') : t('anatomy.design'),
  };
  const segments: AnatomySegment[] = anatomy.segments.map((segment) => ({
    key: segment.key,
    // A back print prefixes the type with `2` (no dash), as the print SKU does.
    text: segment.key === 'type' && anatomy.prefix === '2' ? `2${segment.text}` : segment.text,
    label: label[segment.key],
    tone: segment.tone,
  }));
  return anatomy.prefix === 'CUST-' ? [{ key: 'custom', text: 'CUST', label: t('anatomy.custom') }, ...segments] : segments;
}

/** The held-back notice lines, one per reason, in a fixed order. */
function heldBackLines(t: TFunction<'productSync'>, preview: SupplyComposePreviewOk): string[] {
  const lines: string[] = [];
  const gap = preview.rows.filter((r) => r.status === 'held_back' && r.reason === 'no_blank_combination');
  if (gap.length > 0) {
    lines.push(t('supply.preview.gap', { count: gap.length, list: nameList(t, gap.map((r) => variantLabel(t, r))) }));
  }
  const inUse = preview.rows.filter((r) => r.status === 'in_use');
  if (inUse.length > 0) {
    const first = inUse[0]!;
    const location = locationOf(preview, first) ?? preview.locations.map((l) => l.name).join(t('supply.preview.listSeparator'));
    lines.push(
      t('supply.preview.inUseCount', {
        count: inUse.length,
        location,
        list: nameList(t, inUse.map((r) => variantLabel(t, r))),
      }),
    );
  }
  const others = new Map<string, SupplyPreviewVariantRow[]>();
  for (const row of preview.rows) {
    if (row.status !== 'held_back' || row.reason === 'no_blank_combination') continue;
    const reason = heldBackReason(t, row);
    others.set(reason, [...(others.get(reason) ?? []), row]);
  }
  for (const [reason, rows] of others) {
    lines.push(
      t('supply.preview.otherHeld', { reason, count: rows.length, list: nameList(t, rows.map((r) => variantLabel(t, r))) }),
    );
  }
  const leftOut = preview.rows.filter((r) => r.status === 'left_out');
  if (leftOut.length > 0) {
    lines.push(t('supply.preview.leftOutCount', { count: leftOut.length, list: nameList(t, leftOut.map((r) => variantLabel(t, r))) }));
  }
  return lines;
}

function PreviewSkeleton() {
  // UI 8: a shimmering anatomy and skeleton rows, never a page spinner.
  return (
    <div className="flex flex-col gap-3" aria-hidden="true" data-testid="supply-preview-skeleton">
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-12 w-16 motion-reduce:animate-none" />
        <Skeleton className="h-12 w-12 motion-reduce:animate-none" />
        <Skeleton className="h-12 w-12 motion-reduce:animate-none" />
        <Skeleton className="h-12 w-20 motion-reduce:animate-none" />
      </div>
      <Skeleton className="h-10 w-full motion-reduce:animate-none" />
      <Skeleton className="h-10 w-full motion-reduce:animate-none" />
      <Skeleton className="h-10 w-full motion-reduce:animate-none" />
    </div>
  );
}

export function SupplyPreviewStep({ preview, ownerName, loading, error, onRetry, idPrefix }: SupplyPreviewStepProps) {
  const { t } = useTranslation('productSync');

  if (error && !loading) {
    return (
      <SectionError
        variant="compact"
        body={t('supply.preview.error')}
        onRetry={onRetry}
        retryLabel={t('supply.preview.retry')}
        data-testid="supply-preview-error"
      />
    );
  }

  if (loading || !preview?.anatomy) {
    return (
      <div aria-busy="true">
        <PreviewSkeleton />
      </div>
    );
  }

  const rows = preview.rows;
  const boxed = rows.length > SUPPLY_PREVIEW_BOX_ROWS;
  const notice = heldBackLines(t, preview);
  const locations = preview.locations.map((l) => l.name).join(t('supply.preview.listSeparator'));
  const headId = `${idPrefix}-preview-head`;

  const table = (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          {[t('supply.preview.colVariant'), t('supply.preview.colPrintSku'), t('supply.preview.colBlank'), t('supply.preview.colStatus')].map(
            (heading) => (
              <th
                key={heading}
                scope="col"
                className={cn('py-2 pr-3 font-medium', boxed && 'sticky top-0 z-10 bg-background')}
              >
                {heading}
              </th>
            ),
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const blank = row.blank
            ? [row.blank.label, row.blank.size, row.blank.colour].filter((p): p is string => !!p && p.trim() !== '').join(' · ')
            : '';
          return (
            <tr key={row.variantId} className="border-t align-top" data-testid="supply-preview-row">
              <td className="py-2 pr-3 font-medium">
                <span className="break-words">{variantLabel(t, row)}</span>
              </td>
              <td className="min-w-0 py-2 pr-3 font-mono break-all">{row.printSku ?? ''}</td>
              <td className="py-2 pr-3 text-muted-foreground">
                <span className="break-words">{blank}</span>
              </td>
              <td className="py-2 pr-3">
                <span className="flex flex-col items-start gap-1">
                  {row.status === 'ready' && <StatusPill tone="on">{t('supply.preview.ready')}</StatusPill>}
                  {row.status === 'left_out' && <StatusPill tone="off">{t('supply.match.leftOut')}</StatusPill>}
                  {row.status === 'held_back' && (
                    <>
                      <StatusPill tone="warning">{t('supply.preview.heldBack')}</StatusPill>
                      <span className="leading-normal text-amber-800">{heldBackReason(t, row)}</span>
                    </>
                  )}
                  {row.status === 'in_use' && (
                    <>
                      <StatusPill tone="warning">{t('supply.preview.inUse')}</StatusPill>
                      <span className="leading-normal text-amber-800">{inUseLine(t, preview, row)}</span>
                    </>
                  )}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="flex flex-col gap-3" data-testid="supply-preview">
      <div className="flex flex-col gap-2">
        <p id={headId} className="text-xs text-muted-foreground">
          {t('tier1.everyVariant')}
        </p>
        <SkuAnatomy size="md" segments={anatomySegments(t, preview.anatomy)} />
      </div>
      {boxed ? (
        // A scrolling region is a tab stop so the keyboard can scroll it (UI 22, UI 25).
        <div
          role="region"
          aria-label={t('supply.step3')}
          tabIndex={0}
          className="max-h-80 overflow-y-auto rounded-md border px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="supply-preview-box"
        >
          {table}
        </div>
      ) : (
        table
      )}
      {notice.length > 0 && (
        <WarningNotice className="text-xs" data-testid="supply-held-back">
          <span className="flex flex-col gap-1 leading-normal">
            {notice.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </span>
        </WarningNotice>
      )}
      <NoteLine icon={Link2} className="leading-normal">
        {t('supply.note.inherits', { owner: ownerName })}
      </NoteLine>
      <NoteLine icon={Boxes} className="leading-normal">
        {t('supply.note.stock', { owner: ownerName, location: locations })}
      </NoteLine>
    </div>
  );
}
